import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { giveAskApi, type GiveAskDirectoryItem, type GiveAskReplyItem } from '@/lib/give-ask-api';

interface AskRepliesSheetProps {
  target: GiveAskDirectoryItem | null;
  onOpenChange: (open: boolean) => void;
}

export function AskRepliesSheet({ target, onOpenChange }: AskRepliesSheetProps) {
  const open = target !== null;
  const [loaded, setLoaded] = useState<{ askId: string; replies: GiveAskReplyItem[] } | null>(null);
  const replies = target && loaded?.askId === target.id ? loaded.replies : null;

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    giveAskApi
      .listReplies(target.id)
      .then((data) => {
        if (cancelled) return;
        setLoaded({ askId: target.id, replies: data.replies });
        for (const reply of data.replies) {
          if (!reply.readAt) void giveAskApi.markReplyRead(target.id, reply.id);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded({ askId: target.id, replies: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>Replies to your ask</SheetTitle>
          {target ? <p className="text-sm text-muted-foreground">{target.title}</p> : null}
        </SheetHeader>
        <div className="mt-4 space-y-3">
          {replies === null ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : replies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No replies yet.</p>
          ) : (
            replies.map((reply) => (
              <div key={reply.id} className="rounded-xl border bg-white p-4">
                <p className="text-sm font-medium">
                  {[reply.fromFirstName, reply.fromLastName].filter(Boolean).join(' ') || 'Member'}
                  {reply.readAt ? null : <span className="ml-2 text-xs text-orange-600">New</span>}
                </p>
                {reply.leadTitle ? <p className="mt-1 text-sm">{reply.leadTitle}</p> : null}
                {reply.leadDescription ? (
                  <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{reply.leadDescription}</p>
                ) : null}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
